<template>
    <IntegrationModal
        title="Lettr"
        :invalid-validation="v$.$invalid"
        :loading="loading"
        :edit-mode="integration !== undefined"
        @close="$emit('close')"
        @create="createIntegration"
        @edit="updateIntegration"
    >
        <template #inputs>
            <div class="mb-6 w-full">
                <TextInput
                    v-model="v$.name.$model"
                    class="mt-6"
                    placeholder="Enter your preferred integration name"
                    label="Name"
                    :error="getError('name')"
                />

                <TextInput
                    v-model="v$.api_key.$model"
                    class="mt-6 w-full"
                    placeholder="Your Lettr API key"
                    label="API key"
                    required
                    :error="getError('api_key')"
                />

                <div class="mt-6 text-sm text-gray-500">
                    Don't have Lettr yet?
                    <a
                        class="text-topol-blue-500 hover:text-topol-blue-600"
                        :href="lettrRegisterUrl()"
                        target="_blank"
                        rel="noopener"
                        >Create a free account</a
                    >
                    and copy the key it gives you.
                </div>
            </div>
        </template>
    </IntegrationModal>
</template>

<script lang="ts" setup>
import { route } from 'ziggy-js';
import { create } from '@composables/useNotifications';
import useVuelidate from '@vuelidate/core';
import { maxLength, required } from '@vuelidate/validators';
import IInputErrors from '@/types/Atomic/IInputErrors';
import { PropType } from 'vue';
import { ILettr, IIntegration } from '@/types/Integrations/IIntegration';
import useIntegrations from '../../composables/useIntegrations';
import { lettrRegisterUrl } from '@/utils/lettrUrls';

const props = defineProps({
    integration: {
        type: Object as PropType<IIntegration<ILettr>>,
        required: false,
        default: undefined,
    },
});
const emit = defineEmits(['close']);

const loading = ref(false);
const errors = ref<IInputErrors>({});
const { lettrIntegrationModalOpened, integrationCreated } = useIntegrations();
const state = reactive({
    name: props.integration?.name || '',
    api_key: '',
});

const rules = {
    name: {
        maxLength: maxLength(255),
    },
    api_key: {
        required,
        maxLength: maxLength(5000),
    },
};

const v$ = useVuelidate(rules, state);

const getError = (field: string) => {
    return v$.value[field]?.$errors[0]?.$message || errors.value?.[field] || '';
};

const createIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error) {
        return;
    } else {
        loading.value = true;
        router.post(
            route('integrations.lettr.store'),
            {
                name: state.name,
                api_key: state.api_key,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    create({
                        title: 'Integration added',
                        text: 'Lettr integration has been successfully added.',
                        type: 'success',
                    });
                    integrationCreated('Lettr');
                    lettrIntegrationModalOpened.value = false;
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: 'Integration not added',
                        text:
                            err.api_key ??
                            err.message ??
                            'There was an error adding Lettr integration.',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};

const updateIntegration = () => {
    v$.value.$touch();
    if (v$.value.$error || !props.integration) {
        return;
    } else {
        loading.value = true;
        router.put(
            route('integrations.lettr.update', {
                integration: props.integration.id,
            }),
            {
                name: state.name,
                api_key: state.api_key,
            },
            {
                preserveScroll: true,
                onSuccess: () => {
                    emit('close');
                    create({
                        title: 'Integration updated',
                        type: 'success',
                    });
                },
                onError: (err) => {
                    errors.value = err;
                    create({
                        title: 'Integration not updated',
                        text:
                            err.api_key ??
                            err.message ??
                            'There was an error updating Lettr integration.',
                        type: 'error',
                    });
                },
                onFinish: () => {
                    loading.value = false;
                },
            }
        );
    }
};
</script>

<style scoped></style>
